/* pzz-offer-countdown.js
 * ----------------------
 * <pzz-offer-countdown data-target="DD/MM/YYYY HH:MM:SS"> drives the countdown
 * markup inside it, using the Apple-style digit-flip animation (ghost-slot
 * collapse + blur/slide exit & enter) ported from the global countdown timer.
 *
 * Defined globally and guarded, so more than one section can load it on the
 * same page without redefining. connectedCallback re-fires on every (re)mount,
 * so the timer survives theme-editor section reloads (shopify:section:load).
 *
 * The structural/animation CSS is injected once into <head> under namespaced
 * `pzz-oc__*` classes — kept separate from the global timer's `.digit-slot` /
 * `.number` so the two can never collide. Per-section font size and colour stay
 * in each section's own CSS; the slots are em-based so they scale to the cell.
 *
 * Note on time zone: the target date is parsed in the *visitor's* local time.
 * Note on digits: values are rendered as 2 digits (leading zero collapses to a
 * single digit, matching the global timer). Days >= 100 would show only the
 * last two digits — fine for any realistic sale window.
 */
(function () {
  var OC_SETTLE_MS = 440;

  /* ---- digit rendering / animation (namespaced port of the global timer) --- */

  function initNumber(el, value) {
    el.classList.add('pzz-oc__num');
    var tens = Math.floor(value / 10);
    var ones = value % 10;
    var tensGhost = tens === 0;
    el.setAttribute('data-value', String(value));
    el.innerHTML =
      '<span class="pzz-oc__slot' + (tensGhost ? ' pzz-oc__slot--ghost' : '') + '" data-pos="tens">' +
        (tensGhost ? '' : '<span class="pzz-oc__char">' + tens + '</span>') +
      '</span>' +
      '<span class="pzz-oc__slot" data-pos="ones">' +
        '<span class="pzz-oc__char">' + ones + '</span>' +
      '</span>';
  }

  function animateSlot(slot, oldChar, newChar) {
    slot.innerHTML = '';
    var exitEl = document.createElement('span');
    exitEl.className = 'pzz-oc__char pzz-oc__char--exit';
    exitEl.textContent = String(oldChar);
    slot.appendChild(exitEl);

    var enterEl = document.createElement('span');
    enterEl.className = 'pzz-oc__char pzz-oc__char--enter';
    enterEl.textContent = String(newChar);
    slot.appendChild(enterEl);

    setTimeout(function () {
      if (slot.querySelector('.pzz-oc__char--enter')) {
        slot.innerHTML = '<span class="pzz-oc__char">' + newChar + '</span>';
      }
    }, OC_SETTLE_MS);
  }

  function collapseSlot(slot, oldChar) {
    slot.innerHTML = '';
    var exitEl = document.createElement('span');
    exitEl.className = 'pzz-oc__char pzz-oc__char--exit';
    exitEl.textContent = String(oldChar);
    slot.appendChild(exitEl);
    slot.classList.add('pzz-oc__slot--ghost');
    setTimeout(function () { slot.innerHTML = ''; }, OC_SETTLE_MS);
  }

  function expandSlot(slot, newChar) {
    slot.classList.remove('pzz-oc__slot--ghost');
    slot.innerHTML = '';
    var enterEl = document.createElement('span');
    enterEl.className = 'pzz-oc__char pzz-oc__char--enter';
    enterEl.textContent = String(newChar);
    slot.appendChild(enterEl);
    setTimeout(function () {
      if (slot.querySelector('.pzz-oc__char--enter')) {
        slot.innerHTML = '<span class="pzz-oc__char">' + newChar + '</span>';
      }
    }, OC_SETTLE_MS);
  }

  function updateNumber(el, newValue) {
    var oldValue = parseInt(el.getAttribute('data-value'), 10);
    if (isNaN(oldValue) || oldValue === newValue) return;
    el.setAttribute('data-value', String(newValue));

    var oldTens = Math.floor(oldValue / 10);
    var newTens = Math.floor(newValue / 10);
    var oldOnes = oldValue % 10;
    var newOnes = newValue % 10;

    var tensSlot = el.querySelector('[data-pos="tens"]');
    var onesSlot = el.querySelector('[data-pos="ones"]');

    if (oldOnes !== newOnes) animateSlot(onesSlot, oldOnes, newOnes);

    if (oldTens !== newTens) {
      if (newTens === 0) collapseSlot(tensSlot, oldTens);
      else if (oldTens === 0) expandSlot(tensSlot, newTens);
      else animateSlot(tensSlot, oldTens, newTens);
    }
  }

  function calcParts(distance) {
    return {
      days: Math.floor(distance / 86400000),
      hours: Math.floor((distance % 86400000) / 3600000),
      minutes: Math.floor((distance % 3600000) / 60000),
      seconds: Math.floor((distance % 60000) / 1000)
    };
  }

  /* ---- custom element ------------------------------------------------------ */

  class PzzOfferCountdown extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.end = this.parse(this.dataset.target);
      this.clock = this.querySelector('[data-clock]');
      this.ended = this.querySelector('[data-ended]');
      this.titleEl = this.querySelector('[data-timer-title]');
      this.cells = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]')
      };

      if (this.end == null) return;

      if (this.end - Date.now() < 0) {
        this.expire();
        return;
      }

      var p = calcParts(this.end - Date.now());
      initNumber(this.cells.days, p.days);
      initNumber(this.cells.hours, p.hours);
      initNumber(this.cells.minutes, p.minutes);
      initNumber(this.cells.seconds, p.seconds);

      this._id = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this._id) clearInterval(this._id);
      this._id = null;
      this._init = false;
    }

    // "DD/MM/YYYY HH:MM:SS" -> epoch ms (local time), or null if unparseable.
    parse(str) {
      if (!str) return null;
      var parts = str.trim().split(/[\s/:]+/);
      if (parts.length < 3) return null;
      var day = parseInt(parts[0], 10);
      var month = parseInt(parts[1], 10) - 1;
      var year = parseInt(parts[2], 10);
      var hour = parseInt(parts[3] || '0', 10);
      var minute = parseInt(parts[4] || '0', 10);
      var second = parseInt(parts[5] || '0', 10);
      var date = new Date(year, month, day, hour, minute, second);
      return isNaN(date.getTime()) ? null : date.getTime();
    }

    expire() {
      if (this._id) clearInterval(this._id);
      this._id = null;
      if (this.clock) this.clock.hidden = true;
      if (this.titleEl) this.titleEl.hidden = true;
      if (this.ended) this.ended.hidden = false;
    }

    tick() {
      var diff = this.end - Date.now();
      if (diff < 0) {
        this.expire();
        return;
      }
      var p = calcParts(diff);
      updateNumber(this.cells.days, p.days);
      updateNumber(this.cells.hours, p.hours);
      updateNumber(this.cells.minutes, p.minutes);
      updateNumber(this.cells.seconds, p.seconds);
    }
  }

  if (!customElements.get('pzz-offer-countdown')) {
    customElements.define('pzz-offer-countdown', PzzOfferCountdown);
  }
})();